import { useCallback, useEffect, useRef, useState } from 'react';
import { SupportAccessService } from '@services/SupportAccessService';
import { notificationHub } from '@/signalr/hubs/NotificationHub';
import type { ISupportRequest } from '@models/supportAccess/supportAccess.types';

/** Запасной опрос, пока есть запрос без ответа: событие хаба могло не дойти (хаб переподключался). */
export const SUPPORT_REQUESTS_POLL = 60_000;
/** Опрос, пока хаб уведомлений не подключён: согласие живёт 2 минуты, событие о нём без хаба не придёт. */
export const SUPPORT_REQUESTS_OFFLINE_POLL = 30_000;
/** Шаг обратного отсчёта плашек. */
const TICK = 1000;

export type SupportRequestAction = 'accept' | 'decline';

/** Запрос со сроком по часам клиента: `deadline` — когда он истечёт (мс), `null` — неизвестно, отсчёта нет. */
export interface ISupportRequestItem extends ISupportRequest {
    deadline: number | null;
}

/**
 * Срок каждого запроса — от момента ответа по оставшемуся времени на сервере (`expiresInSeconds`, иначе `expiresAt`
 * минус время сервера из заголовка `Date`). Часы клиента, спешащие на минуты, иначе прятали бы согласие сразу.
 * Без того и другого (заголовок скрыт CORS) срока нет: запрос виден, пока его отдаёт сервер.
 */
export function withDeadlines(
    requests: ISupportRequest[],
    receivedAt: number,
    serverDate: string | undefined,
): ISupportRequestItem[] {
    const serverNow = serverDate ? Date.parse(serverDate) : NaN;
    return requests.map((request) => {
        const expires = Date.parse(request.expiresAt);
        const left =
            typeof request.expiresInSeconds === 'number' && Number.isFinite(request.expiresInSeconds)
                ? request.expiresInSeconds * 1000
                : Number.isFinite(serverNow) && Number.isFinite(expires)
                  ? expires - serverNow
                  : null;
        return { ...request, deadline: left === null ? null : receivedAt + Math.max(0, left) };
    });
}

const notExpired = (request: ISupportRequestItem, now: number) => request.deadline === null || request.deadline > now;
const visible = () => document.visibilityState === 'visible';

/**
 * Запросы поддержки к пользователю (IMPLEMENTATION.md §15): читаются при входе, по событию `SupportRequest`
 * хаба уведомлений (создание и любая смена статуса — отменённый или принятый запрос уходит из списка), после
 * переподключения хаба, при возврате на вкладку, раз в 30 с, пока хаб не подключён, и раз в минуту, пока хоть один
 * ждёт ответа; запасные опросы — только в видимой вкладке. Истёкшие по сроку прячутся сразу, не дожидаясь
 * чистильщика сервера.
 */
export function useSupportRequests(enabled: boolean) {
    const [requests, setRequests] = useState<ISupportRequestItem[]>([]);
    const [busy, setBusy] = useState<Record<string, SupportRequestAction>>({});
    const [now, setNow] = useState(() => Date.now());
    const generation = useRef(0);

    const reload = useCallback(async () => {
        const current = ++generation.current;
        try {
            const { data, headers } = await SupportAccessService.fetchRequests();
            if (current !== generation.current) return;
            const date = headers?.date;
            setRequests(
                withDeadlines(Array.isArray(data) ? data : [], Date.now(), typeof date === 'string' ? date : undefined),
            );
        } catch {
            // Сеть или сервер без запросов поддержки: плашки остаются как были, следующий опрос повторит.
        }
    }, []);

    useEffect(() => {
        if (!enabled) {
            generation.current++;
            setRequests([]);
            return undefined;
        }
        void reload();
        const unsubscribe = notificationHub.onSupportRequest(() => void reload());
        const unsubscribeReconnected = notificationHub.onReconnected(() => void reload());
        const offline = window.setInterval(() => {
            if (!notificationHub.isConnected && visible()) void reload();
        }, SUPPORT_REQUESTS_OFFLINE_POLL);
        const onVisibility = () => {
            if (visible()) void reload();
        };
        document.addEventListener('visibilitychange', onVisibility);
        const loads = generation;
        return () => {
            // Ответ, пришедший после отписки, не применяется.
            loads.current++;
            unsubscribe();
            unsubscribeReconnected();
            clearInterval(offline);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [enabled, reload]);

    const pending = enabled && requests.length > 0;
    useEffect(() => {
        if (!pending) return undefined;
        const poll = window.setInterval(() => {
            if (visible()) void reload();
        }, SUPPORT_REQUESTS_POLL);
        return () => clearInterval(poll);
    }, [pending, reload]);
    // Секундный отсчёт — только у согласия со сроком (его видно в плашке); у прочих запросов со сроком — один таймер
    // к ближайшему: истёкший прячется сразу.
    const counting = enabled && requests.some((request) => request.kind === 'consent' && request.deadline !== null);
    const nearest = enabled ? Math.min(...requests.map((request) => request.deadline ?? Infinity)) : Infinity;
    useEffect(() => {
        if (!pending) return undefined;
        setNow(Date.now());
        if (counting) {
            const tick = window.setInterval(() => setNow(Date.now()), TICK);
            return () => clearInterval(tick);
        }
        if (!Number.isFinite(nearest)) return undefined;
        const expire = window.setTimeout(() => setNow(Date.now()), Math.max(0, nearest - Date.now()) + 1);
        return () => clearTimeout(expire);
    }, [pending, counting, nearest]);

    const respond = useCallback(
        async (id: string, action: SupportRequestAction): Promise<boolean> => {
            setBusy((current) => ({ ...current, [id]: action }));
            try {
                if (action === 'accept') await SupportAccessService.accept(id);
                else await SupportAccessService.decline(id);
                generation.current++;
                setRequests((list) => list.filter((request) => request.id !== id));
                return true;
            } catch {
                // Текст отказа показал общий перехватчик; запрос мог уже истечь или быть отменён — перечитываем.
                void reload();
                return false;
            } finally {
                setBusy((current) => {
                    const next = { ...current };
                    delete next[id];
                    return next;
                });
            }
        },
        [reload],
    );

    return {
        requests: requests.filter((request) => notExpired(request, now)),
        now,
        busy,
        reload,
        accept: useCallback((id: string) => respond(id, 'accept'), [respond]),
        decline: useCallback((id: string) => respond(id, 'decline'), [respond]),
    };
}
